import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  collection,
  doc,
  documentId,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useFormTheme } from '../../../contexts/FormThemeContext';
import { useFormSubmissionSettings } from '../../../contexts/FormSubmissionSettingsContext';
import RichTextEditor2 from '../RichTextEditor2'; // Importing the TextEditor component
import { replaceChipsWithValues } from "../../../utils/formUtils"; // Import chip replacement helper
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Import the copy icon
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning'

const FIRESTORE_IN_QUERY_LIMIT = 10;

function chunkArray(items = [], size = FIRESTORE_IN_QUERY_LIMIT) {
  const chunks = [];

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
}

// Component starts here
const DisplayCardPreview = ({
  submittedValues = {},
  mode = "builder",
  renderOutputAsOutlinedField = false,
}) => {
  const { cardStyle } = useFormTheme(); // Dynamic styling from FormThemeContext
  const { submissionSettings } = useFormSubmissionSettings(); // Get submission settings
  //  Debugging Log: Check if submittedValues updates correctly
  console.log("[DisplayCardPreview] Received submittedValues:", submittedValues);
  console.log("[DisplayCardPreview] Current submissionSettings.displayInCard:", submissionSettings?.displayInCard);

  // Retrieve "displayInCard" settings or fallback defaults.
  // Note: customMessage here is expected to be the original rich text template with chip placeholders.
  const {
    cardTitle = 'Submission Successful',
    cardDescription = 'Thank you for your submission.',
    customMessage = '',
    errorMessage,
    copyIconEnabled,
    enableBitly,
    submittedRows = [],
    displayCardOutputRows: runtimeDisplayCardOutputRows = [],
    latestSubmissionDocId,
    latestSubmissionDocIds = [],
    integrationRuntime = {},
    bitlyShortUrl: runtimeBitlyShortUrl,
    bitlyShortlinkStatus: runtimeBitlyStatus,
    shortlinks: runtimeShortlinks,
  } = submissionSettings?.displayInCard || {};


  // Determine if we have a valid submission (i.e. submittedValues contains keys)
  const hasValidSubmission = submittedValues && Object.keys(submittedValues).length > 0;

  // Row-aware Display Card runtime output:
  // - FormSubmissionsContext emits displayCardOutputRows only for true bulk
  //   Display Card submissions.
  // - Single-row submissions intentionally clear displayCardOutputRows and
  //   continue using the lean submittedValues + latestSubmissionDocId path.
  // - submittedRows remains supported as a backwards-compatible fallback for
  //   existing bulk Display Card rendering.
  const displayCardRows = React.useMemo(() => {
    if (
      Array.isArray(runtimeDisplayCardOutputRows) &&
      runtimeDisplayCardOutputRows.length > 0
    ) {
      return runtimeDisplayCardOutputRows
        .filter(Boolean)
        .map((row, idx) => ({
          rowIndex: Number.isFinite(row.rowIndex) ? row.rowIndex : idx,
          clientRowId: row.clientRowId || null,
          submissionId: row.submissionId || null,
          submissionDocId:
            row.submissionDocId ||
            (
              Array.isArray(latestSubmissionDocIds)
                ? latestSubmissionDocIds[idx]
                : latestSubmissionDocId
            ) ||
            null,
          persistenceStatus: row.persistenceStatus || null,
          generatedText: String(row.generatedText || '').trim(),
          integrationRuntime: row.integrationRuntime || {},
        }))
        .filter((row) => Boolean(row.generatedText));
    }

    if (Array.isArray(submittedRows) && submittedRows.length > 0) {
      return submittedRows
        .map((rowText, idx) => ({
          rowIndex: idx,
          clientRowId: null,
          submissionId: null,
          submissionDocId:
            (
              Array.isArray(latestSubmissionDocIds)
                ? latestSubmissionDocIds[idx]
                : latestSubmissionDocId
            ) ||
            null,
          persistenceStatus: null,
          generatedText: String(rowText || '').trim(),
          integrationRuntime: {},
        }))
        .filter((row) => Boolean(row.generatedText));
    }

    return [];
  }, [
    runtimeDisplayCardOutputRows,
    submittedRows,
    latestSubmissionDocId,
    latestSubmissionDocIds,
  ]);

  const hasDisplayCardRuntimeRows = displayCardRows.length > 0;

  // Bulk: runtime-only submitted rows (set by FormSubmissionsContext via patchActionRuntime)
  // IMPORTANT:
  // - A single generated URL should not enter the bulk rendering path.
  // - True bulk output is any row-aware runtime set with more than one row.
  const hasBulkRows =
    displayCardRows.length > 1 ||
    (
      Array.isArray(submittedRows) &&
      submittedRows.length > 1
    );

  const shouldRenderDisplayCardRows = displayCardRows.length > 1;

  // Retrieve theme primary color
  const themePrimaryColor = cardStyle?.primaryColor || "#007bff"; // Default to blue if undefined

  // Local state to track copy button click
  const [copied, setCopied] = useState(false);

  // Bulk: track which row was copied (so only that row highlights)
  const [copiedRowIndex, setCopiedRowIndex] = useState(null);

  // Enterprise copy feedback:
  // - Works for standalone public forms and iframe embeds.
  // - Does not change submission data, display card data, or copy semantics.
  // - Gives users visible confirmation instead of relying only on icon color.
  const [copySnackbar, setCopySnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  // State for backend-generated short URL copy feedback.
  const [isShortenedURLCopied, setIsShortenedURLCopied] = useState(false);

  // Bulk: track which backend-generated short URL row was copied.
  const [copiedShortUrlRowKey, setCopiedShortUrlRowKey] = useState(null);

  // Display-only Bitly hydration:
  // - The backend owns Bitly creation and credentials.
  // - FormSubmissionsContext passes persisted submission ids.
  // - This component listens only to the saved submission docs needed
  //   to hydrate the Display Card short URL fields.
  // - Single submissions use a direct doc listener.
  // - Bulk submissions use chunked Firestore "in" queries so the UI maps
  //   "Generated URL N" to the matching "Short URL N" without backend
  //   changes, polling, or blocking submission persistence.
  const [hydratedBitlyBySubmissionId, setHydratedBitlyBySubmissionId] = useState({});

  const singleSubmissionDocIdForHydration =
    !shouldRenderDisplayCardRows
      ? latestSubmissionDocId || null
      : null;

  const bulkSubmissionDocIdsForHydration = React.useMemo(() => {
    if (!shouldRenderDisplayCardRows) {
      return [];
    }

    const ids = [
      ...(Array.isArray(latestSubmissionDocIds) ? latestSubmissionDocIds : []),
      ...displayCardRows.map((row) => row.submissionDocId),
    ]
      .filter(Boolean)
      .map(String);

    return Array.from(new Set(ids));
  }, [
    shouldRenderDisplayCardRows,
    latestSubmissionDocIds,
    displayCardRows,
  ]);

  useEffect(() => {
    const hasDisplayOutput =
      hasValidSubmission ||
      hasDisplayCardRuntimeRows;

    if (
      mode !== 'final' ||
      enableBitly !== true ||
      !hasDisplayOutput
    ) {
      return undefined;
    }

    // ────────────────────────────────────────────────────────────────
    // Single generated URL path:
    // - Keep this lean: one Firestore doc listener.
    // - Do not treat single submission as bulk.
    // ────────────────────────────────────────────────────────────────
    if (!shouldRenderDisplayCardRows) {
      if (!singleSubmissionDocIdForHydration) {
        return undefined;
      }

      const activeSubmissionIds = new Set([singleSubmissionDocIdForHydration]);

      setHydratedBitlyBySubmissionId((prev) => {
        let changed = false;

        const next = Object.keys(prev || {}).reduce((acc, submissionDocId) => {
          if (activeSubmissionIds.has(submissionDocId)) {
            acc[submissionDocId] = prev[submissionDocId];
          } else {
            changed = true;
          }

          return acc;
        }, {});

        return changed ? next : prev;
      });

      const submissionRef = doc(
        db,
        'formSubmissions',
        singleSubmissionDocIdForHydration
      );

      const unsubscribe = onSnapshot(
        submissionRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            return;
          }

          const data = snapshot.data() || {};
          const shortUrl =
            data.bitlyShortUrl ||
            data.shortlinks?.bitly?.shortUrl ||
            null;

          const status =
            data.bitlyShortlinkStatus ||
            data.shortlinks?.bitly?.status ||
            null;

          if (!shortUrl && !status) {
            return;
          }

          console.log('[DisplayCardPreview] Bitly hydration snapshot:', {
            latestSubmissionDocId: singleSubmissionDocIdForHydration,
            status,
            shortUrl,
          });

          setHydratedBitlyBySubmissionId((prev) => ({
            ...prev,
            [singleSubmissionDocIdForHydration]: {
              bitlyShortUrl: shortUrl,
              bitlyShortlinkStatus: status,
              shortlinks: data.shortlinks || null,
            },
          }));
        },
        (error) => {
          console.warn('[DisplayCardPreview] Bitly hydration failed:', {
            latestSubmissionDocId: singleSubmissionDocIdForHydration,
            error: error?.message || error,
          });
        }
      );

      return () => {
        unsubscribe();
      };
    }

    // ────────────────────────────────────────────────────────────────
    // Bulk generated URL path:
    // - Listen to only the persisted submission docs needed by the
    //   rendered generated rows.
    // - Chunk "in" queries to avoid query operand limits.
    // ────────────────────────────────────────────────────────────────
    if (bulkSubmissionDocIdsForHydration.length === 0) {
      return undefined;
    }

    const activeSubmissionIds = new Set(bulkSubmissionDocIdsForHydration);

    setHydratedBitlyBySubmissionId((prev) => {
      let changed = false;

      const next = Object.keys(prev || {}).reduce((acc, submissionDocId) => {
        if (activeSubmissionIds.has(submissionDocId)) {
          acc[submissionDocId] = prev[submissionDocId];
        } else {
          changed = true;
        }

        return acc;
      }, {});

      return changed ? next : prev;
    });

    const idChunks = chunkArray(bulkSubmissionDocIdsForHydration);

    const unsubscribers = idChunks.map((idChunk) => {
      const submissionsQuery = query(
        collection(db, 'formSubmissions'),
        where(documentId(), 'in', idChunk)
      );

      return onSnapshot(
        submissionsQuery,
        (snapshot) => {
          snapshot.docs.forEach((submissionDoc) => {
            const submissionDocId = submissionDoc.id;
            const data = submissionDoc.data() || {};

            const shortUrl =
              data.bitlyShortUrl ||
              data.shortlinks?.bitly?.shortUrl ||
              null;

            const status =
              data.bitlyShortlinkStatus ||
              data.shortlinks?.bitly?.status ||
              null;

            if (!shortUrl && !status) {
              return;
            }

            console.log('[DisplayCardPreview] Bitly hydration snapshot:', {
              latestSubmissionDocId: submissionDocId,
              status,
              shortUrl,
            });

            setHydratedBitlyBySubmissionId((prev) => ({
              ...prev,
              [submissionDocId]: {
                bitlyShortUrl: shortUrl,
                bitlyShortlinkStatus: status,
                shortlinks: data.shortlinks || null,
              },
            }));
          });
        },
        (error) => {
          console.warn('[DisplayCardPreview] Bitly hydration failed:', {
            latestSubmissionDocIds: idChunk,
            error: error?.message || error,
          });
        }
      );
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [
    bulkSubmissionDocIdsForHydration,
    enableBitly,
    hasDisplayCardRuntimeRows,
    hasValidSubmission,
    mode,
    shouldRenderDisplayCardRows,
    singleSubmissionDocIdForHydration,
  ]);

  const runtimeBitlyIntegration = integrationRuntime?.bitly || null;

  const primarySubmissionDocId =
    !shouldRenderDisplayCardRows
      ? latestSubmissionDocId || null
      : null;

  const primaryHydratedBitly =
    primarySubmissionDocId
      ? hydratedBitlyBySubmissionId[primarySubmissionDocId]
      : null;

  const bitlyShortUrl =
    primaryHydratedBitly?.bitlyShortUrl ||
    primaryHydratedBitly?.shortlinks?.bitly?.shortUrl ||
    runtimeBitlyShortUrl ||
    runtimeShortlinks?.bitly?.shortUrl ||
    submittedValues?.bitlyShortUrl ||
    submittedValues?.shortlinks?.bitly?.shortUrl ||
    null;

  const bitlyShortlinkStatus =
    primaryHydratedBitly?.bitlyShortlinkStatus ||
    primaryHydratedBitly?.shortlinks?.bitly?.status ||
    runtimeBitlyStatus ||
    runtimeBitlyIntegration?.status ||
    submittedValues?.bitlyShortlinkStatus ||
    submittedValues?.shortlinks?.bitly?.status ||
    null;

  const getBitlyStateForSubmission = React.useCallback(({
    submissionDocId = null,
    rowIntegrationRuntime = {},
  } = {}) => {
    const hydrated =
      submissionDocId
        ? hydratedBitlyBySubmissionId[submissionDocId]
        : primaryHydratedBitly;

    const rowBitlyRuntime = rowIntegrationRuntime?.bitly || null;

    const shortUrl =
      hydrated?.bitlyShortUrl ||
      hydrated?.shortlinks?.bitly?.shortUrl ||
      (
        !submissionDocId
          ? bitlyShortUrl
          : null
      );

    const status =
      hydrated?.bitlyShortlinkStatus ||
      hydrated?.shortlinks?.bitly?.status ||
      rowBitlyRuntime?.status ||
      runtimeBitlyStatus ||
      runtimeBitlyIntegration?.status ||
      (
        !submissionDocId
          ? bitlyShortlinkStatus
          : null
      );

    const displayText =
      shortUrl ||
      (
        status === 'error'
          ? 'Short URL unavailable'
          : 'Getting URL...'
      );

    return {
      shortUrl,
      status,
      displayText,
      canCopy: Boolean(shortUrl),
    };
  }, [
    bitlyShortUrl,
    bitlyShortlinkStatus,
    hydratedBitlyBySubmissionId,
    primaryHydratedBitly,
    runtimeBitlyIntegration,
    runtimeBitlyStatus,
  ]);

  const shouldShowBitlyField =
    mode === 'final' &&
    enableBitly === true &&
    hasValidSubmission &&
    !shouldRenderDisplayCardRows &&
    (
      Boolean(bitlyShortUrl) ||
      bitlyShortlinkStatus === 'queued' ||
      bitlyShortlinkStatus === 'processing' ||
      bitlyShortlinkStatus === 'error' ||
      Boolean(latestSubmissionDocId)
    );

  const shouldShowBitlyFieldForRow = React.useCallback((row) => {
    const rowBitly = getBitlyStateForSubmission({
      submissionDocId: row?.submissionDocId || null,
      rowIntegrationRuntime: row?.integrationRuntime || {},
    });

    return (
      mode === 'final' &&
      enableBitly === true &&
      shouldRenderDisplayCardRows &&
      Boolean(row?.generatedText) &&
      (
        Boolean(rowBitly.shortUrl) ||
        rowBitly.status === 'queued' ||
        rowBitly.status === 'processing' ||
        rowBitly.status === 'error' ||
        Boolean(row?.submissionDocId)
      )
    );
  }, [
    enableBitly,
    getBitlyStateForSubmission,
    mode,
    shouldRenderDisplayCardRows,
  ]);

  // Native MUI small outlined field for the backend-generated Short URL.
  // The Generated URL field remains custom because it can render rich HTML.
  // The Short URL is plain text only, so MUI TextField keeps the UI simpler,
  // smaller, and more consistent with the rest of the application.
  const bitlyHierarchyContainerStyles = {
    mt: 1.25,
    width: '100%',
    minWidth: 0,
    display: 'flex',
    alignItems: 'stretch',
  };

  const bitlyTreeGuideStyles = {
    flex: { xs: '0 0 22px', sm: '0 0 28px' },
    position: 'relative',
    minHeight: '40px',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-8px',
      bottom: '50%',
      left: { xs: '10px', sm: '13px' },
      borderLeft: '1px solid rgba(0, 0, 0, 0.18)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: { xs: '10px', sm: '13px' },
      width: { xs: '10px', sm: '12px' },
      borderTop: '1px solid rgba(0, 0, 0, 0.18)',
    },
  };

  // Determine whether to show the header (card title and description):
  // - Always show header in builder mode.
  // - In final mode, show header only if there's a valid submission.
  const showHeader =
    mode === "builder" ||
    (
      mode === "final" &&
      (hasValidSubmission || hasDisplayCardRuntimeRows)
    );

  // Determine the message to display:
  // - If in "final" mode and there are no submitted values (i.e. invalid submission),
  //   display the error message from context.
  // - Otherwise, process the original template with the submitted values.
  let messageToDisplay = "";
  if (mode === "final" && !hasValidSubmission && !hasBulkRows && !hasDisplayCardRuntimeRows) {
    // No valid submission and no bulk rows → show error
    messageToDisplay = errorMessage;
  } else {
    // Non-bulk path (or builder mode): resolve from submittedValues
    messageToDisplay = replaceChipsWithValues(customMessage, submittedValues, mode);
  }


  const showCopySnackbar = (message, severity = 'success') => {
    setCopySnackbar({
      open: true,
      severity,
      message,
    });
  };

  const handleCopySnackbarClose = (event, reason) => {
    // MUI best practice: ignore clickaway so accidental outside clicks do not
    // immediately dismiss feedback before users can read it.
    if (reason === 'clickaway') return;

    setCopySnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const writeTextToClipboard = async (textToCopy) => {
    const normalizedText =
      typeof textToCopy === 'string'
        ? textToCopy
        : String(textToCopy ?? '');

    if (!normalizedText.trim()) {
      throw new Error('Nothing available to copy.');
    }

    // Primary path: modern async Clipboard API.
    // Parent iframe must allow clipboard-write when embedded cross-origin.
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function'
    ) {
      await navigator.clipboard.writeText(normalizedText);
      return;
    }

    // Fallback path for older browsers or constrained runtimes.
    // This is only used if navigator.clipboard.writeText is unavailable.
    const textarea = document.createElement('textarea');
    textarea.value = normalizedText;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    textarea.style.opacity = '0';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const copiedSuccessfully = document.execCommand('copy');

      if (!copiedSuccessfully) {
        throw new Error('Fallback copy command failed.');
      }
    } finally {
      document.body.removeChild(textarea);
    }
  };

  // Define a function for copying text to the clipboard
const handleCopyClick = async () => {
  try {
    // Convert the HTML string to plain text using DOMParser.
    const parser = new DOMParser();
    const doc = parser.parseFromString(messageToDisplay, 'text/html');
    const plainText = doc.body.textContent || "";

    await writeTextToClipboard(plainText);
//    console.log("Copied to clipboard:", plainText);

    // Set copied state to true to change both icon & text color
    setCopied(true);
    showCopySnackbar('Copied to clipboard.', 'success');

    // Reset copied state 
    setTimeout(() => {
      setCopied(false);
    }, 300);
  } catch (err) {
    console.error("Failed to copy: ", err);
    showCopySnackbar('Copy failed. Please select and copy the text manually.', 'error');
  }
};

// Function for copying the backend-generated short URL to the clipboard
const handleShortenedUrlCopyClick = async (
  shortUrlToCopy = bitlyShortUrl,
  rowKey = 'single'
) => {
  const normalizedShortUrl =
    typeof shortUrlToCopy === 'string'
      ? shortUrlToCopy
      : String(shortUrlToCopy ?? '');

  if (!normalizedShortUrl.trim()) {
    showCopySnackbar('Short URL is not ready yet.', 'info');
    return;
  }

  try {
    await writeTextToClipboard(normalizedShortUrl);
//    console.log("Shortened URL copied to clipboard:", normalizedShortUrl);

    //  Set copied state for shortened URL
    setIsShortenedURLCopied(true);
    setCopiedShortUrlRowKey(rowKey);
    showCopySnackbar('Shortened URL copied to clipboard.', 'success');

    //  Reset copied state after a short delay
    setTimeout(() => {
      setIsShortenedURLCopied(false);
      setCopiedShortUrlRowKey(null);
    }, 300);
  } catch (err) {
    console.error("Failed to copy shortened URL: ", err);
    showCopySnackbar('Copy failed. Please select and copy the URL manually.', 'error');
  }
};

  // Updated Container styles (static)
  // const containerStyles = {
  //   backgroundColor: "#e0e0e0",
  //   width: "calc(100% + 4rem)",
  //   marginLeft: "-2rem",
  //   marginRight: "-2rem",
  //   padding: "30px 0",
  //   display: "flex",
  //   justifyContent: "center",
  // };

  // Updated Container styles (static)
  const containerStyles = {
    backgroundColor: '#e0e0e0',
    padding: '30px 0px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  };

  // Card styles with dynamic integration from FormThemeContext
  const cardStyles = {
    backgroundColor: cardStyle?.backgroundColor || '#ffffff',
    border: `1px solid ${cardStyle?.borderColor || '#d9d9d9'}`,
    borderRadius: cardStyle?.borderRadius || '4px',
    padding: cardStyle?.padding || '16px',
    fontSize: cardStyle?.fontSize || '14px',
    lineHeight: cardStyle?.lineHeight || 1.5,
    color: cardStyle?.textColor || '#595959',
    maxWidth: '80%',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box', // Ensure padding is included in width calculation
  };

  // Define a container for the content and copy icon using flex layout.
  // Adjusted spacing values to reduce vertical gap.
  const contentContainerStyles = {
    display: 'flex',
    alignItems: 'center',  // Vertically center the items
    marginTop: '0px',      // Removed margin-top to reduce space below description
    padding: '2px 2px',        // Removed padding for minimal spacing
  };

  // Style for the copy icon: smaller than text and with a smaller right margin.
  const copyIconStyles = {
    fontSize: '1rem',   // Smaller than default text size
    marginRight: '8px', // Reduced right margin
    marginLeft: '8px',
  };

  // Live published Display Card output shell:
  // - Mimics the outlined MUI field pattern from UTMViewer
  // - Keeps rich-text/HTML output intact
  // - Keeps copy behavior inside the field
  // - Does not affect builder mode unless renderOutputAsOutlinedField is explicitly true
  //
  // Enterprise UI notes:
  // - The field uses a custom rich-content shell instead of TextField so we do not
  //   flatten or lose the user's rich-text Display Card formatting.
  // - The floating label is vertically centered over the outline using the same
  //   visual principle as MUI's outlined input label/notch behavior.
  // - The content row is vertically centered for single-line output, while still
  //   allowing rich/multi-line content to wrap naturally.
  const renderOutlinedRichOutput = ({
    label = 'Generated URL',
    copiedState = false,
    onCopy,
    showCopyButton = false,
    children,
  }) => (
    <Box
      sx={{
        mt: 2,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        minHeight: '56px',
        px: 1.5,
        py: 0,
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        transition: 'border-color 150ms ease-in-out',
        '&:hover': {
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{
          position: 'absolute',
          top: 0,
          left: '12px',
          transform: 'translateY(-50%)',
          px: 0.5,
          backgroundColor: '#ffffff',
          color: 'rgba(0, 0, 0, 0.6)',
          lineHeight: 1,
          fontSize: '0.75rem',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        {label}
      </Typography>

      {showCopyButton && (
        <IconButton
          onClick={onCopy}
          size="small"
          aria-label={`Copy ${label}`}
          sx={{
            flex: '0 0 auto',
            width: 32,
            height: 32,
            p: 0.5,
            color: copiedState ? themePrimaryColor : 'inherit',
          }}
        >
          {copiedState ? (
            <CheckCircleIcon color="success" sx={{ fontSize: '1rem' }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: '1rem' }} />
          )}
        </IconButton>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: '40px',
          py: '8.5px',
          display: 'flex',
          alignItems: 'center',
          fontSize: cardStyle?.fontSize || '16px',
          fontFamily: cardStyle?.fontFamily || 'Arial',
          color: copiedState ? themePrimaryColor : cardStyle?.textColor || '#000000',
          lineHeight: cardStyle?.lineHeight || 1.5,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          '& > *': {
            width: '100%',
          },
          '& p': {
            mt: 0,
            mb: 0.5,
          },
          '& p:last-child': {
            mb: 0,
          },
          '& ul, & ol': {
            mt: 0.5,
            mb: 0.5,
            pl: 3,
          },
          '& a': {
            color: 'inherit',
            textDecoration: 'underline',
            overflowWrap: 'anywhere',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );

  const renderBitlyShortUrlField = ({
    submissionDocId = null,
    rowKey = 'single',
    rowIntegrationRuntime = {},
  } = {}) => {
    const bitlyState = getBitlyStateForSubmission({
      submissionDocId,
      rowIntegrationRuntime,
    });

    const isCopied =
      copiedShortUrlRowKey === rowKey ||
      (
        rowKey === 'single' &&
        isShortenedURLCopied
      );

    const hasShortUrl = Boolean(bitlyState.shortUrl);

    const shortUrlFieldValue =
      bitlyState.shortUrl ||
      (
        bitlyState.status === 'error'
          ? 'Short URL unavailable'
          : 'Generating URL...'
      );

    const bitlyShortUrlTextColor = isCopied
      ? themePrimaryColor
      : hasShortUrl
        ? 'rgba(0, 0, 0, 0.62)'
        : bitlyState.status === 'error'
          ? 'rgba(211, 47, 47, 0.72)'
          : 'rgba(0, 0, 0, 0.42)';

    return (
      <Box sx={bitlyHierarchyContainerStyles}>
        <Box aria-hidden="true" sx={bitlyTreeGuideStyles} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            label="Short URL"
            size="small"
            variant="outlined"
            fullWidth
            value={shortUrlFieldValue}
            error={bitlyState.status === 'error' && !bitlyState.shortUrl}
            InputProps={{
              readOnly: true,
              startAdornment: copyIconEnabled && bitlyState.canCopy ? (
                <InputAdornment position="start">
                  <IconButton
                    onClick={() => handleShortenedUrlCopyClick(
                      bitlyState.shortUrl,
                      rowKey
                    )}
                    edge="start"
                    size="small"
                    aria-label="Copy Short URL"
                    sx={{
                      color: isCopied
                        ? themePrimaryColor
                        : 'rgba(0, 0, 0, 0.54)',
                    }}
                  >
                    {isCopied ? (
                      <CheckCircleIcon color="success" sx={{ fontSize: '1rem' }} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                fontFamily: cardStyle?.fontFamily || 'Arial',
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '0.875rem',
                color: bitlyShortUrlTextColor,
                lineHeight: 1.4,
                fontStyle: hasShortUrl ? 'normal' : 'italic',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.8125rem',
                color: 'rgba(0, 0, 0, 0.56)',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'rgba(0, 0, 0, 0.64)',
              },
              '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(211, 47, 47, 0.5)',
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={containerStyles}>
      <Box sx={cardStyles}>
        { showHeader && (
          <>
            {/* Dynamic Card Title */}
            <Typography
              variant="h6"
//              gutterBottom
              align={cardStyle?.textAlign || 'left'}
              sx={{ color: cardStyle?.titleColor || 'inherit' }}
            >
              {cardTitle}
            </Typography>

            {/* Dynamic Card Description */}
            <Typography
              variant="body2"
              align={cardStyle?.textAlign || 'left'}
              sx={{
                color: cardStyle?.descriptionColor || 'inherit',
                mb: 0 // Remove bottom margin to reduce spacing between description and content
              }}
            >
              {cardDescription}
            </Typography>
          </>
        )}
        {/* If we are in "final" mode AND there's no valid submission, show warning icon + error text */}
        {mode === "final" && !hasValidSubmission && !hasBulkRows && !hasDisplayCardRuntimeRows ? (
          <Box
            sx={{
              mt: 0,
              display: 'flex',
              alignItems: 'center',
              color: 'red',      // or cardStyle?.warningColor if you prefer a dynamic color
              fontSize: '14px',  // Smaller text
            }}
          >
            <WarningIcon sx={{ mr: 1 }} /> 
            <Typography variant="body2" component="span">
              {errorMessage}
            </Typography>
          </Box>
        ) : mode === "builder" ? (
          <Box
            sx={{
              marginTop: '0px',  // Reduced margin-top from 2px to 0px
              padding: '0px',     // Removed internal padding
              borderRadius: '4px',
              backgroundColor: '#f9f9f9',
              ...contentContainerStyles,
            }}
          >
            {copyIconEnabled && (
              <IconButton 
              onClick={handleCopyClick} 
              disableRipple
              size="small" 
              sx={{ 
                  p: 0, 
                  color: copied ? themePrimaryColor : "inherit", 
                  '&:hover': { backgroundColor: "transparent" }, 
                  '&:focus': { backgroundColor: "transparent" }, 
                  '&:active': { backgroundColor: "transparent" } 
                }} 
              >
                <ContentCopyIcon sx={copyIconStyles} />
              </IconButton>
            )}
            <RichTextEditor2
              value={messageToDisplay} // Use the computed messageToDisplay here
              styles={{
                fontSize: cardStyle?.fontSize || '16px',
                fontFamily: cardStyle?.fontFamily || 'Arial',
                textColor: cardStyle?.textColor || '#000000',
                textAlign: cardStyle?.textAlign || 'left',
              }}
              readOnly={true}  // Display as read-only post submission
            />
          </Box>
        ) : (
          <Box
            sx={
              renderOutputAsOutlinedField
                ? { width: '100%' }
                : { marginTop: '0px', padding: '0px 8px', ...contentContainerStyles }
            }
          >

          {mode === "final" && shouldRenderDisplayCardRows ? (
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  mt: 0,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1, // space between rows
                }}
              >
                {displayCardRows.map((row, idx) => {
                  const rowKey =
                    row.clientRowId ||
                    row.submissionId ||
                    row.submissionDocId ||
                    `row_${idx}`;

                  const rowText = row.generatedText;

                  const handleBulkRowCopyClick = async () => {
                    try {
                      await writeTextToClipboard(String(rowText ?? ""));
                      setCopiedRowIndex(idx);
                      showCopySnackbar('Copied successfully.', 'success');
                      setTimeout(() => setCopiedRowIndex(null), 300);
                    } catch (err) {
                      console.error("Failed to copy row: ", err);
                      showCopySnackbar('Copy failed.', 'error');
                    }
                  };

                  if (renderOutputAsOutlinedField) {
                    return (
                      <Box
                        key={rowKey}
                        sx={{
                          width: "100%",
                        }}
                      >
                        {/* Per-row copy icon inside the generated output field */}
                        {renderOutlinedRichOutput({
                          label: displayCardRows.length > 1 ? `Generated URL ${idx + 1}` : 'Generated URL',
                          copiedState: copiedRowIndex === idx,
                          onCopy: handleBulkRowCopyClick,
                          showCopyButton: copyIconEnabled,
                          children: (
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: cardStyle?.fontSize || '16px',
                                color: copiedRowIndex === idx ? themePrimaryColor : "inherit",
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                flex: 1,
                              }}
                            >
                              {rowText}
                            </Typography>
                          ),
                        })}

                        {shouldShowBitlyFieldForRow(row) && renderBitlyShortUrlField({
                          submissionDocId: row.submissionDocId,
                          rowKey,
                          rowIntegrationRuntime: row.integrationRuntime,
                        })}
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={rowKey}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          width: "100%",
                        }}
                      >
                        {/* Per-row copy icon (left of the message) */}
                        {copyIconEnabled && (
                          <IconButton
                            onClick={handleBulkRowCopyClick}
                            disableRipple
                            size="small"
                            sx={{
                              p: 0,
                              mt: "2px",
                              mr: "8px",
                              color: copiedRowIndex === idx ? themePrimaryColor : "inherit",
                              '&:hover': { backgroundColor: "transparent" },
                              '&:focus': { backgroundColor: "transparent" },
                              '&:active': { backgroundColor: "transparent" }
                            }}
                          >
                            <ContentCopyIcon sx={copyIconStyles} />
                          </IconButton>
                        )}

                        {/* The row text */}
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: cardStyle?.fontSize || '16px',
                            color: copiedRowIndex === idx ? themePrimaryColor : "inherit",
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            flex: 1,
                          }}
                        >
                          {rowText}
                        </Typography>
                      </Box>

                      {shouldShowBitlyFieldForRow(row) && renderBitlyShortUrlField({
                        submissionDocId: row.submissionDocId,
                        rowKey,
                        rowIntegrationRuntime: row.integrationRuntime,
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (

              <>
                {renderOutputAsOutlinedField ? (
                  renderOutlinedRichOutput({
                    label: 'Generated URL',
                    copiedState: copied,
                    onCopy: handleCopyClick,
                    showCopyButton: copyIconEnabled && hasValidSubmission,
                    children: (
                      <Box
                        sx={{
                          fontSize: cardStyle?.fontSize || '16px',
                          color: copied ? themePrimaryColor : "inherit",
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}
                        dangerouslySetInnerHTML={{ __html: messageToDisplay }}
                      />
                    ),
                  })
                ) : (
                  <>
                    {copyIconEnabled && hasValidSubmission && (
                      <IconButton
                        onClick={handleCopyClick}
                        size="small"
                        disableRipple
                        sx={{
                          p: 0,
                          color: copied ? themePrimaryColor : "inherit",
                          '&:hover': { backgroundColor: "transparent" },
                          '&:focus': { backgroundColor: "transparent" },
                          '&:active': { backgroundColor: "transparent" }
                        }}
                      >
                        <ContentCopyIcon sx={copyIconStyles} />
                      </IconButton>
                    )}

                    <Box
                      sx={{
                        fontSize: cardStyle?.fontSize || '16px',
                        color: copied ? themePrimaryColor : "inherit",
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                      dangerouslySetInnerHTML={{ __html: messageToDisplay }}
                    />
                  </>
                )}
              </>
            )}

          </Box>
        )}


{/* Backend-generated Bitly Short URL Section */}
{!shouldRenderDisplayCardRows && shouldShowBitlyField && renderBitlyShortUrlField({
  submissionDocId: primarySubmissionDocId,
  rowKey: 'single',
  rowIntegrationRuntime: {
    bitly: runtimeBitlyIntegration,
  },
})}



    </Box>

    <Snackbar
      open={copySnackbar.open}
      autoHideDuration={3000}
      onClose={handleCopySnackbarClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={handleCopySnackbarClose}
        severity={copySnackbar.severity}
        variant="filled"
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 'calc(100vw - 32px)',
          alignItems: 'center',
          '& .MuiAlert-message': {
            fontSize: '0.875rem',
            lineHeight: 1.4,
          },
        }}
      >
        {copySnackbar.message}
      </Alert>
    </Snackbar>
  </Box>
);

};

export default DisplayCardPreview;
